import React from "react";
import { Hammer, Droplets, Ruler, CheckCircle2 } from "lucide-react";
import { BRAND } from "../lib/brand";

const PRODUCTS = [
  {
    id: "commercial",
    icon: Hammer,
    eyebrow: "MR / BWR Grade · IS : 303",
    name: "Commercial Plywood",
    summary:
      "Used for furniture, interior work, offices and residential projects.",
    points: [
      "Durable and cost-effective",
      "Smooth finish, ready for lamination",
      "Ideal for indoor furniture and partitions",
    ],
    image: BRAND.images.commercialPlywood,
  },
  {
    id: "waterproof",
    icon: Droplets,
    eyebrow: "BWP · IS : 710",
    name: "Waterproof Plywood",
    summary:
      "High moisture resistance for kitchens, bathrooms and humid areas.",
    points: [
      "Boiling waterproof (BWP) technology",
      "Termite & borer resistant",
      "Long-lasting performance in wet zones",
    ],
    image: BRAND.images.waterproofPlywood,
  },
  {
    id: "calibrated",
    icon: Ruler,
    eyebrow: "Premium Grade",
    name: "Calibrated Plywood",
    headline: "Precision in Every Millimeter.",
    summary:
      "Perfectly calibrated plywood with uniform thickness, superior surface finish, and enhanced bonding strength.",
    points: [
      "Uniform thickness across every panel",
      "Superior, ready-to-finish surface",
      "Enhanced bonding for long-term reliability",
    ],
    image: BRAND.images.calibratedPlywood,
  },
];

export default function Products() {
  return (
    <div data-testid="products-page">
      {/* HERO */}
      <section className="paper-grain">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-24 lg:py-28">
          <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
            Our Products
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl text-wood-800 mt-4 leading-[1.03] max-w-3xl">
            Built for every space.<br />Engineered for every season.
          </h1>
          <p className="mt-6 text-wood-700 max-w-2xl leading-relaxed">
            From everyday interiors to precision-calibrated premium plywood,
            Sylva King&apos;s range covers every requirement with the same
            uncompromising commitment to strength, finish and longevity.
          </p>
        </div>
      </section>

      {/* PRODUCT LIST */}
      <section className="bg-wood-100 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 space-y-16 lg:space-y-24">
          {PRODUCTS.map((p, idx) => {
            const Icon = p.icon;
            const isReversed = idx % 2 === 1;
            return (
              <div
                key={p.id}
                data-testid={`product-${p.id}`}
                id={p.id}
                className={`grid lg:grid-cols-12 gap-10 items-center ${
                  isReversed ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-6 img-zoom rounded-sm overflow-hidden border border-wood-200 bg-wood-50">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-[440px] object-contain"
                    data-testid={`product-${p.id}-img`}
                  />
                </div>
                <div className="lg:col-span-6">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Icon className="text-gold-600 shrink-0" size={28} />
                    <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                      {p.eyebrow}
                    </div>
                  </div>
                  <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-4 leading-[1.05]">
                    {p.name}
                  </h2>
                  {p.headline && (
                    <div className="font-serif text-2xl sm:text-3xl text-gold-600 italic mt-3">
                      {p.headline}
                    </div>
                  )}
                  <p className="mt-5 text-wood-700 leading-relaxed">{p.summary}</p>
                  <ul className="mt-7 space-y-3">
                    {p.points.map((pt, i) => (
                      <li key={i} className="flex items-start gap-3 text-wood-700">
                        <CheckCircle2 className="text-gold-600 shrink-0 mt-0.5" size={18} />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
